/* tslint:disable */
import {Injectable, Injector} from '@angular/core';
import {Neuron, NeuronsGroup} from "./neuronsModels";
import {WebGLService} from "../views/three-dview/web-gl.service";
import {TwoDService} from "../views/two-dview/two-d.service";
import {BehaviorSubject, timer} from "rxjs";
import {NgxSpinnerService} from "ngx-spinner";
import {AtlasSharedService} from "../atlas-shared.service";
import {AlertService} from "../../alert.service";
import {NeuronsSearchService} from "./neurons-search.service";
import {Color} from "../drawingDataModels";
import {environment} from "../../../environments/environment";
import {LayoutService} from "../layout/layout.service";
import {StatusMessageService} from "../status-message.service";
import {TabService} from "../../tab.service";

@Injectable({
    providedIn: 'root'
})
export class NeuronsService {


    neuronsGroups = {};
    neuronsGroupsIDs = [];

    userSelectedNeuronsIDs = [];

    subjectIsNeuronsGroupsReady = new BehaviorSubject(false);


    neuronsTracing = {
        entire: true,
        opacity: 0.2,
        shownSegmentLength: null
    }
    entireNeuronTracing = true;

    constructor(private webGLService: WebGLService,
                private atlasSharedService: AtlasSharedService,
                private twoDService: TwoDService,
                private spinner: NgxSpinnerService,
                private injector: Injector,
                private alertService: AlertService,
                private tabService: TabService,
                private statusMessageService: StatusMessageService) {
    }

    async fetchAndParseNeurons(neuronsIDs, fileType) {
        await this.statusMessageService.updateStatus(`Fetching neuron data...`);

        const start = performance.now();
        let neuronsSearchService = this.injector.get<NeuronsSearchService>(NeuronsSearchService);
        let threshold = neuronsSearchService.neuronsLoadingParameters.threshold;
        let chunks = [];

        // Notify UI about chunk loading
        await this.statusMessageService.updateStatus(`Loading ${neuronsIDs.length} Neurons from backend...`);

        for (let i = 0; i < neuronsIDs.length; i += threshold) {
            // this.spinner.show();
            // console.log(i);
            let neuronsIDsChunk = neuronsIDs.slice(i, i + threshold);
            const options = {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({neuronsIDs: neuronsIDsChunk, fileType: fileType}),
            };

            let urlGetNeuronsData = environment.apiURL + 'get_neurons_data';
            chunks.push(fetch(`${urlGetNeuronsData}/`, options).then(response => response.arrayBuffer()));

            await this.statusMessageService.updateStatus(` ${i} neurons loaded out of ${neuronsIDs.length}...`);
        }

        await this.statusMessageService.updateStatus(`Parsing neurons SWC files`, true); // true, To make the three dots (. → .. → ... → . → .. → ...) appear dynamically until the next message replaces it

        // Fetch all chunks in parallel
        let responses = await Promise.all(chunks);

        let objectsList = await Promise.all(responses.map(response => neuronsSearchService.parseChunkBinary(response)));
        let mergedList = [].concat(...objectsList);

        await this.statusMessageService.updateStatus(
            `All ${neuronsIDs.length} neurons processed.`,
            false,
            true, // Enable auto-hide
            0.5 // Hide after 0.5 or 2 seconds, depending on the chosen view
        );

        const end = performance.now();
        console.log("Time to finish ==> ", end - start);

        // console.log("All neurons loaded:", mergedList);

        return mergedList;
    }

    async loadNeuronsIntoScenes(groupID, neuronsDataList, groupName, colors, isNBLASTResult) {
        let layoutService = this.injector.get<LayoutService>(LayoutService);

        await this.statusMessageService.updateStatus(`Initializing neuron group...`);

        let neuronGroup = await this.initNeuronsGroup(groupID, groupName, neuronsDataList, colors, isNBLASTResult);
        this.spinner.hide();

        await this.statusMessageService.updateStatus(`Rendering neurons...`);

        const {threeD} = layoutService.viewOptions;
        let timeToHideNeuronsStatusMessage = 2000;

        if (threeD) {
            timeToHideNeuronsStatusMessage = 500;
            await this.addChunkOfNeuronsTo3DScene(
                neuronGroup, neuronsDataList, 0, neuronsDataList.length
            );
        }
        // The 2D scene update is always needed
        await this.addNeuronsGroupTo2DScene(neuronGroup, true);

        // If not threeD, then update the 3D scene after the 2D one
        if (!threeD) {
            this.addChunkOfNeuronsTo3DScene(
                neuronGroup, neuronsDataList, 0, neuronsDataList.length
            );
        }

        console.log(timeToHideNeuronsStatusMessage)
        await this.statusMessageService.updateStatus(
            `Drawing complete. Total neurons rendered: ${neuronsDataList.length}`,
            false,
            true, // Enable auto-hide
            timeToHideNeuronsStatusMessage // Hide after 0.5 or 2 seconds, depending on the chosen view
        );
    }


    getAppendingNeuronsGroup() {
        for (let id in this.neuronsGroups) {
            let currentGroup = this.neuronsGroups[id];
            if (currentGroup.settings.isAppendNeurons) {
                return currentGroup;
            }
        }
        return null;
    }

    getNeuronGroupData(groupID) {
        return this.neuronsGroups[groupID];
    }

    openNeuronGroupPanel(currentSelectedNeuronGroupID) {
        for (let i = 0; i < this.neuronsGroupsIDs.length; i++) {
            let neuronGroupID = this.neuronsGroupsIDs[i];
            let arrowElement = document.getElementById('neuronGroupPanelArrow' + currentSelectedNeuronGroupID.toString());
            // to open current selected panel group
            if (neuronGroupID === currentSelectedNeuronGroupID) {
                if (!arrowElement.classList.contains('in')) {
                    arrowElement.click();
                }
            } else {
                //To close other panel groups
                if (arrowElement.classList.contains('in')) {
                    arrowElement.click();
                }
            }
        }
    }

    initNeuronsGroup(groupID, groupName, neurons, colors, isNBLASTResult) {
        let group;
        if (groupID == null) {
            let groupData = {
                group_name: groupName,
                neurons: neurons
            };
            group = this.createNeuronsGroup(groupData, isNBLASTResult, colors);
            groupID = group.id;
        } else {
            group = this.neuronsGroups[groupID];
        }
        return group;
    }

    createNeuronsGroup(groupData, isNBLASTResult, neuronsColor = []) {
        let newGroup = new NeuronsGroup(groupData.group_name, groupData.neurons, true, isNBLASTResult, neuronsColor);
        this.neuronsGroups[newGroup.id] = newGroup;
        this.neuronsGroupsIDs.push(newGroup.id);
        setTimeout(() => {
            this.openNeuronGroupPanel(newGroup.id);
        }, 5); // it was 1000
        return newGroup;
    }

    drawNeuronsGradually(chunkSize: number, startNeuronIndex: number, neuronsGroup: NeuronsGroup, progress: number) {
        setTimeout(() => {

            let chunk = neuronsGroup.neuronsData.neuronsIDs.slice(startNeuronIndex, startNeuronIndex + chunkSize);

            for (let i = 0; i < chunkSize; i++) {
                progress = startNeuronIndex + i;
                if (progress >= neuronsGroup.neuronsData.neuronsIDs.length) {
                    break;
                }
                let currentNeuron = neuronsGroup.neuronsData.neurons[chunk[i]];
                this.addNeuronTo3DScene(currentNeuron, neuronsGroup);
            }

            startNeuronIndex += chunkSize;
            if (startNeuronIndex < neuronsGroup.neuronsData.neuronsIDs.length) {
                this.drawNeuronsGradually(chunkSize, startNeuronIndex, neuronsGroup, progress);
            } else {
                this.addNeuronsGroupTo2DScene(neuronsGroup, true);
            }

        }, 5);
    }

    async addChunkOfNeuronsTo3DScene(group: NeuronsGroup, newNeuronsList, startIndex: number, endIndex: number) {
        //adding neurons in case of appending neurons to a neuron group
        await group.addNeurons(newNeuronsList);
        await this.addChunkOfNeuronsGroupTo3DScene(group, startIndex, endIndex);
    }

    receiveNeuronsFromBackend(groupData, isNBLASTResult, isAddNeuronsGradually, neuronsColor = []) {
        // this.tabService.openNeuronsTab();

        if (groupData.neurons.length > 0) {
            let group = this.getAppendingNeuronsGroup();
            if (group) {
                group.addNeurons(groupData.neurons, neuronsColor);
            } else {
                group = this.createNeuronsGroup(groupData, isNBLASTResult, neuronsColor);
            }
            this.subjectIsNeuronsGroupsReady.next(true);

            if (isAddNeuronsGradually) {
                let chunkSize = 15;
                this.drawNeuronsGradually(chunkSize, 0, group, 0);
            } else {
                // this.addNeuronsGroupTo2DScene(group);
            }

        } else {
            this.alertService.showWarningMsg('No neurons were found!')
        }
    }


    async addNeuronTo3DScene(neuron: Neuron, neuronsGroup: NeuronsGroup) {

        if (!neuron.isMeshCreated()) {
            await neuron.createMesh();
        }

        if (neuronsGroup.visualizationOptions.show) {
            if (neuron.selected) {
                this.webGLService.mouse.actionModes.neuronsSelection.meshes.push(neuron.activeFile.meshes.somaMesh);
                this.webGLService.mouse.actionModes.neuronsSelection.info.push({
                    neuronID: neuron.id,
                    groupID: neuronsGroup.id
                });

                this.webGLService.webGLVariables.scene.add(neuron.activeFile.meshes.somaMesh);
                if (!neuronsGroup.visualizationOptions.isOnlySoma) {
                    this.webGLService.webGLVariables.scene.add(neuron.activeFile.meshes.activeMesh);
                }
            }
        }
    };

    removeNeuronFrom3DScene(neuron, neuronsGroup: NeuronsGroup) {
        if ((neuron.activeFile.meshes !== null) && (neuron.activeFile.meshes !== undefined)) {
            let index = this.webGLService.mouse.actionModes.neuronsSelection.meshes.indexOf(neuron.activeFile.meshes.somaMesh);
            this.webGLService.mouse.actionModes.neuronsSelection.meshes.splice(index, 1);
            index = this.webGLService.mouse.actionModes.neuronsSelection.info.indexOf({
                neuronID: neuron.id,
                groupID: neuronsGroup.id
            });
            this.webGLService.mouse.actionModes.neuronsSelection.info.splice(index, 1);
            this.webGLService.webGLVariables.scene.remove(neuron.activeFile.meshes.somaMesh);
            if (!neuronsGroup.visualizationOptions.isOnlySoma) {
                this.webGLService.webGLVariables.scene.remove(neuron.activeFile.meshes.activeMesh);
            }

        }
    };

    async addNeuronsGroupTo2DScene(neuronsGroup: NeuronsGroup, isUpdate = true) {
        if (neuronsGroup.visualizationOptions.show) {
            this.twoDService.addNeuronsGroupTo2DScene(neuronsGroup, isUpdate);
        }
    };

    removeNeuronsGroupfrom2DScene(neuronsGroup: NeuronsGroup, isUpdate = true) {
        this.twoDService.removeNeuronsGroupFrom2DScene(neuronsGroup, isUpdate);
    };

    removeNeuronsGroupFrom3DScene(neuronsGroup: NeuronsGroup) {
        for (let id in neuronsGroup.neuronsData.neurons) {
            this.removeNeuronFrom3DScene(neuronsGroup.neuronsData.neurons[id], neuronsGroup);
        }
    };

    addNeuronsGroupTo3DScene(neuronsGroup: NeuronsGroup) {
        for (let id in neuronsGroup.neuronsData.neurons) {
            this.addNeuronTo3DScene(neuronsGroup.neuronsData.neurons[id], neuronsGroup);
        }
    };

    addChunkOfNeuronsGroupTo3DScene(neuronsGroup: NeuronsGroup, startIndex: number, endIndex: number) {
        let neuronsIDs = neuronsGroup.neuronsData.neuronsIDs.slice(startIndex, endIndex);
        for (let id in neuronsIDs) {
            this.addNeuronTo3DScene(neuronsGroup.neuronsData.neurons[neuronsIDs[id]], neuronsGroup);
        }
    };

    addNeuronsGroupTo3DSceneAtOnce(neuronsGroup: NeuronsGroup) {
        for (let id in neuronsGroup.neuronsData.neurons) {
            this.addNeuronTo3DScene(neuronsGroup.neuronsData.neurons[id], neuronsGroup);
        }
    };

    onGroupSelectAllChange(neuronGroup) {
        this.removeNeuronsGroupFrom3DScene(neuronGroup);

        for (let id in neuronGroup.neuronsData.neurons) {
            neuronGroup.neuronsData.neurons[id].selected = neuronGroup.settings.isSelectAll;
        }

        if (neuronGroup.settings.isSelectAll) {
            this.addNeuronsGroupTo3DSceneAtOnce(neuronGroup);
        }
        this.twoDService.updateScene();

        neuronGroup.settings.isShowLabel = false;
    };

    onGroupOnlySomaChange(onlySomaSelected, neuronGroupData) {
        if (onlySomaSelected) {
            for (let id in neuronGroupData.neuronsData.neurons) {
                let neuron = neuronGroupData.neuronsData.neurons[id];
                this.webGLService.webGLVariables.scene.remove(neuron.activeFile.meshes.activeMesh);
            }
        } else {
            for (let id in neuronGroupData.neuronsData.neurons) {
                let neuron = neuronGroupData.neuronsData.neurons[id];
                this.webGLService.webGLVariables.scene.add(neuron.activeFile.meshes.activeMesh);
            }
        }
        this.twoDService.updateScene();
    };

    async onGroupThicknessChange(neuronsGroup) {
        this.spinner.show();
        this.removeNeuronsGroupFrom3DScene(neuronsGroup);
        for (let id in neuronsGroup.neuronsData.neurons) {
            let neuron = neuronsGroup.neuronsData.neurons[id];
            if (neuronsGroup.visualizationOptions.drawingOptions.method === NeuronsGroup.neuronsDrawingMethods.lines) {
                await neuron.activeFile.createLinesMesh(neuron.activeFile.data.nodes, neuron.visualizationOptions.color, []);
                neuron.activeFile.meshes.activeMesh = neuron.activeFile.meshes.linesMesh;
            } else if (neuronsGroup.visualizationOptions.drawingOptions.method === NeuronsGroup.neuronsDrawingMethods.points) {
                neuron.activeFile.meshes.pointsMesh = undefined;
                await neuron.activeFile.createPointsMesh(neuron.activeFile.data.nodes, neuron.visualizationOptions.color, neuronsGroup.visualizationOptions.drawingOptions.drawingResolution, neuronsGroup.visualizationOptions.drawingOptions.thickness);
                neuron.activeFile.meshes.activeMesh = neuron.activeFile.meshes.pointsMesh;
            } else {
                neuron.activeFile.meshes.cylinderMesh = undefined;
                await neuron.activeFile.createCylinderMesh(neuron.activeFile.data.nodes, neuron.visualizationOptions.color, neuronsGroup.visualizationOptions.drawingOptions.drawingResolution, neuronsGroup.visualizationOptions.drawingOptions.thickness);
                neuron.activeFile.meshes.activeMesh = neuron.activeFile.meshes.cylinderMesh;
            }
            this.addNeuronTo3DScene(neuron, neuronsGroup);
            // this.atlasSharedService.hideSpinner(200);
            this.spinner.hide();
        }
    }

    async updateNeuronsGroupActiveFile(neuronsGroup) {
        this.spinner.show();
        let neuronsGroupRemovedFrom3DScene = false;
        for (let id in neuronsGroup.neuronsData.neurons) {
            let neuron = neuronsGroup.neuronsData.neurons[id];
            // console.log("neuron.jsonData[neuronsGroup.visualizationOptions.neuronsVersion]", neuron.jsonData[neuronsGroup.visualizationOptions.neuronsVersion]);
            if (neuron.jsonData[neuronsGroup.visualizationOptions.neuronsVersion].jsonData != undefined) {
                if (neuronsGroupRemovedFrom3DScene == false) {
                    this.removeNeuronsGroupFrom3DScene(neuronsGroup);
                    neuronsGroupRemovedFrom3DScene = true;
                }
                neuron.activeFile = neuron.jsonData[neuronsGroup.visualizationOptions.neuronsVersion];
                neuron.activeFile.meshes.somaMesh = undefined;
                neuron.activeFile.meshes.linesMesh = undefined;
                neuron.activeFile.meshes.pointsMesh = undefined;
                neuron.activeFile.meshes.cylinderMesh = undefined;
                await neuron.activeFile.load();
            } else {
                this.alertService.showWarningMsg('No neurons were found!');
            }
        }
        this.addNeuronsGroupTo3DScene(neuronsGroup);
        this.addNeuronsGroupTo2DScene(neuronsGroup);
        this.spinner.hide();
    }

    getSelectedNeuronData(neuronsData) {
        let data = {
            filteredNeuronsIDs: [],
            colors: []
        };
        for (let j = 0; j < neuronsData.filteredNeuronsIDs.length; j++) {
            let neuronID = neuronsData.filteredNeuronsIDs[j];
            let currentNeuronData = neuronsData.neurons[neuronID];
            if (currentNeuronData.selected) {
                data['filteredNeuronsIDs'].push(neuronID);
                data['colors'].push(currentNeuronData.visualizationOptions.color.hex);
            }
        }
        return data;
    }

    getSelectedNeuronsGroupShareView() {
        let data = [];
        for (let i = 0; i < this.neuronsGroupsIDs.length; i++) {
            let neuronsGroupsID = this.neuronsGroupsIDs[i];
            let neuronsGroupsData = this.neuronsGroups[neuronsGroupsID];
            if (neuronsGroupsData.visualizationOptions.show) {
                data.push({
                    id: neuronsGroupsID,
                    priority: i,
                    name: neuronsGroupsData.name,
                    neuronsData: this.getSelectedNeuronData(neuronsGroupsData.neuronsData),
                    settings: {
                        canBeDownloaded: neuronsGroupsData.settings.canBeDownloaded,
                        isOverwriteNeurons: neuronsGroupsData.settings.isOverwriteNeurons,
                        isSelectAll: neuronsGroupsData.settings.isSelectAll,
                        isAppendNeurons: neuronsGroupsData.settings.isAppendNeurons,
                        isShowLabel: neuronsGroupsData.settings.isShowLabel
                    },
                    visualizationOptions: {
                        color: neuronsGroupsData.visualizationOptions.color,
                        drawingOptions: {
                            drawingResolution: neuronsGroupsData.visualizationOptions.drawingOptions.drawingResolution,
                            method: {
                                id: neuronsGroupsData.visualizationOptions.drawingOptions.method.id
                            },
                            minResolution: neuronsGroupsData.visualizationOptions.drawingOptions.minResolution,
                            thickness: neuronsGroupsData.visualizationOptions.drawingOptions.thickness,
                            userResolution: neuronsGroupsData.visualizationOptions.drawingOptions.userResolution,
                        },
                        isOnlySoma: neuronsGroupsData.visualizationOptions.isOnlySoma,
                        neuronsVersion: neuronsGroupsData.visualizationOptions.neuronsVersion,
                        somaSize: neuronsGroupsData.visualizationOptions.somaSize,
                    }
                })
            }
        }
        return data;
    }

    async changeNeuronColor(neuron, color) {
        let newColor = new Color(color)
        // neuron.visualizationOptions.color = newColor;
        neuron.updateColor(newColor);
    }

    changeNeuronActiveFile(neuron, neuronsVersion) {
        neuron.activeFile = neuron.files[neuronsVersion];
        neuron.activeFile.load();
    }

    changeNeuronSomaSize(neuron, somSize) {
        neuron.visualizationOptions.somaSize = somSize;
    }

    async updateNeuronsGroupsSettingsShareView(loadedData) {
        //This delay is crucial, as it allows the neuron groups to be prepared for updating its settings based on the user's previous intention.
        await this.atlasSharedService.delay(1000);
        this.subjectIsNeuronsGroupsReady.subscribe(isNeuronsGroupsReady => {
            if (isNeuronsGroupsReady == true) {
                let neuronsDrawingMethods = [
                    NeuronsGroup.neuronsDrawingMethods.lines,
                    NeuronsGroup.neuronsDrawingMethods.points,
                    NeuronsGroup.neuronsDrawingMethods.cylinder,
                ];

                if (this.atlasSharedService.sharedViewItems.neurons.hasSelectedNeurons) {
                    this.atlasSharedService.sharedViewItems.neurons.isLoaded = true;
                }

                for (let i = 0; i < this.neuronsGroupsIDs.length; i++) {
                    let neuronsGroupsID = this.neuronsGroupsIDs[i];
                    let currentNeuronsGroupData = this.neuronsGroups[neuronsGroupsID];

                    currentNeuronsGroupData.settings.canBeDownloaded = loadedData[currentNeuronsGroupData.name].settings.canBeDownloaded;
                    currentNeuronsGroupData.settings.isAppendNeurons = loadedData[currentNeuronsGroupData.name].settings.isAppendNeurons;
                    currentNeuronsGroupData.settings.isOverwriteNeurons = loadedData[currentNeuronsGroupData.name].settings.isOverwriteNeurons;
                    currentNeuronsGroupData.settings.isSelectAll = loadedData[currentNeuronsGroupData.name].settings.isSelectAll;
                    currentNeuronsGroupData.settings.isShowLabel = loadedData[currentNeuronsGroupData.name].settings.isShowLabel;

                    currentNeuronsGroupData.visualizationOptions.color = new Color(loadedData[currentNeuronsGroupData.name].visualizationOptions.color);
                    currentNeuronsGroupData.visualizationOptions.drawingOptions.method = neuronsDrawingMethods[(loadedData[currentNeuronsGroupData.name].visualizationOptions.drawingOptions.method.id) - 1];
                    currentNeuronsGroupData.visualizationOptions.drawingResolution = loadedData[currentNeuronsGroupData.name].visualizationOptions.drawingOptions.drawingResolution;
                    // currentNeuronsGroupData.visualizationOptions.drawingOptions.minResolution = loadedData[currentNeuronsGroupData.name].visualizationOptions.drawingOptions.minResolution;
                    currentNeuronsGroupData.visualizationOptions.drawingOptions.thickness = loadedData[currentNeuronsGroupData.name].visualizationOptions.drawingOptions.thickness;
                    currentNeuronsGroupData.visualizationOptions.drawingOptions.userResolution = loadedData[currentNeuronsGroupData.name].visualizationOptions.drawingOptions.userResolution;
                    currentNeuronsGroupData.updateNeuronsThickness();
                    this.onGroupThicknessChange(currentNeuronsGroupData);

                    currentNeuronsGroupData.onGroupSomaSizeChange(loadedData[currentNeuronsGroupData.name].visualizationOptions.somaSize);

                    currentNeuronsGroupData.visualizationOptions.isOnlySoma = loadedData[currentNeuronsGroupData.name].visualizationOptions.isOnlySoma;
                    this.onGroupOnlySomaChange(loadedData[currentNeuronsGroupData.name].visualizationOptions.isOnlySoma, currentNeuronsGroupData);

                    currentNeuronsGroupData.visualizationOptions.neuronsVersion = loadedData[currentNeuronsGroupData.name].visualizationOptions.neuronsVersion;
                    if (currentNeuronsGroupData.visualizationOptions.neuronsVersion == 'left' || currentNeuronsGroupData.visualizationOptions.neuronsVersion == 'right') {
                        this.updateNeuronsGroupActiveFile(currentNeuronsGroupData);
                    }

                    //Loop over the neurons to change colors, soma size and the active file to be drawn (left,right, original).
                    for (let j = 0; j < loadedData[currentNeuronsGroupData.name].neuronsData.filteredNeuronsIDs.length; j++) {
                        let neuronID = loadedData[currentNeuronsGroupData.name].neuronsData.filteredNeuronsIDs[j];
                        // this.changeNeuronColor(currentNeuronsGroupData.neuronsData.neurons[neuronID], loadedData[currentNeuronsGroupData.name].neuronsData.filteredNeuronsColors[j]);
                        if (currentNeuronsGroupData.visualizationOptions.neuronsVersion == 'left' || currentNeuronsGroupData.visualizationOptions.neuronsVersion == 'right') {
                            this.changeNeuronActiveFile(currentNeuronsGroupData.neuronsData.neurons[neuronID], loadedData[currentNeuronsGroupData.name].visualizationOptions.neuronsVersion);
                        }
                        this.changeNeuronSomaSize(currentNeuronsGroupData.neuronsData.neurons[neuronID], loadedData[currentNeuronsGroupData.name].visualizationOptions.somaSize);
                    }
                    this.twoDService.updateScene()
                }
            }
        });

    }

    async loadSelectedNeuronsGroupsShareView(neuronsGroupsResponse) {
        let groupsNames = neuronsGroupsResponse['groups_names'];
        let loadedData = neuronsGroupsResponse['data'];
        if (!!groupsNames.length) {
            this.tabService.openNeuronsTab();
            await this.statusMessageService.updateStatus("Start loading Neurons", true,true,2000);
            let neuronsSearchService = this.injector.get<NeuronsSearchService>(NeuronsSearchService);
            await neuronsSearchService.searchForNeuronsIDsByShareViewLink(loadedData, groupsNames);
            await this.updateNeuronsGroupsSettingsShareView(loadedData);
            // this.tabService.openNeuronsTab();
        }
    }

    async extractUserSelectedNeuronIDs() {
        return new Promise((resolve) => {
            this.userSelectedNeuronsIDs = [];
            for (let i = 0; i < this.neuronsGroupsIDs.length; i++) {
                let currentGroup = this.neuronsGroups[this.neuronsGroupsIDs[i]];
                let currentGroupSelected = currentGroup["visualizationOptions"]["show"];
                // console.log("currentGroup ==>", currentGroup["visualizationOptions"]["show"]);
                let neuronsIDs = currentGroup['neuronsData']['neuronsIDs'];
                for (let n = 0; n < neuronsIDs.length; n++) {
                    let currentNeuronID = neuronsIDs[n];
                    let currentNeuron = currentGroup['neuronsData']['neurons'][currentNeuronID];
                    // If current group is selected and the neurons is selected
                    if ((currentGroupSelected) && (currentNeuron.selected)) {
                        if (!this.userSelectedNeuronsIDs.includes(currentNeuron.id)) {
                            this.userSelectedNeuronsIDs.push(currentNeuron.id);
                        }
                    } else if (!currentGroupSelected) { // If current group's show is false, then we have to remove all its neurons from userSelectedNeuronsIDs list
                        if (this.userSelectedNeuronsIDs.includes(currentNeuron.id)) {
                            const index = this.userSelectedNeuronsIDs.indexOf(currentNeuron.id);
                            if (index > -1) {
                                this.userSelectedNeuronsIDs.splice(index, 1);
                            }
                        }
                    }

                }
            }
            // console.log('Data fully loaded');
            resolve(true);
        });
    }

}
